import { Injectable } from '@angular/core';
import { EndpointService } from './endpoint.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
    AlertDeliveryLog,
    AlertEventResponse,
    AlertEventTransitionResponse,
    AlertStateTransition,
    AppNotification,
    FilteredNotificationPayload,
    NotificationDeliveryGroup,
} from '../models/notification';
import {
    AcknowledgePublicAlertResponseResult,
    CapturePublicAlertResponsePayload,
    PublicAlertResponseControlOption,
    PublicAlertResponseEvent,
    PublicAlertResponseNode,
    PublicAlertResponsePageData,
    PublicAlertResponseSubmitted,
} from '../models/public-alert-response.model';
import {
    AlertDeliveryChannel,
    AlertDeliveryStatus,
    AlertEventState,
    AlertEventTransitionSource,
    AlertSeverityLevel,
} from '../enums/alert.enum';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { IGridService } from '../shared/grid/grid.service';
import { GridModel } from '../shared/grid/grid.model';
import { ColumnDetails } from '../models/columnDetails.model';
import { CommonService } from './common.service';
import { DateFormatService } from './dateFormat.service';
import { ToastService } from './toast.service';
import { environment } from 'src/environments/environment';
import { getRegionBaseUrl } from '../_helpers/region.helper';
import { ACK_LINK_ROUTE_SEGMENT } from '../shared/constants/alertResponse.constants';

// TEMPORARY local-preview scaffolding for DASH-3076 while the real endpoints
// (DASH-3188 / DASH-3210 / DASH-3133) are still unbuilt - opening
// /alert-response/<one of these tokens> serves static data instead of
// calling the API, so the page can be reviewed before the backend exists.
// Delete MOCK_ALERT_RESPONSE_TOKENS and buildMockAlertResponsePageData, and
// the branches that check them, once a real backend is in place.
const MOCK_ALERT_RESPONSE_TOKENS = ['mock', 'mock-acked', 'mock-nomap', 'mock-nocapture'] as const;
type MockAlertResponseToken = (typeof MOCK_ALERT_RESPONSE_TOKENS)[number];

// GridModel clamps a missing or zero pageSize to 1 (GridModel.cs), so every non-grid
// GetAlertEvents call must send one. They are sized well above a realistic count for
// what is asked: a user's unacknowledged alerts, or one node's events in a chart window.
const ACTIVE_ALERT_EVENTS_PAGE_SIZE = 500;
const NODE_ALERT_EVENTS_PAGE_SIZE = 1000;

@Injectable({
    providedIn: 'root',
})
export class NotificationService
    extends EndpointService
    implements IGridService<AppNotification>
{
    constructor(
        http: HttpClient,
        private commonService: CommonService,
        private dateFormatService: DateFormatService,
        private toastService: ToastService,
    ) {
        super(http);
    }

    get(
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        filterBy: string,
    ): Observable<GridModel<AppNotification>> {
        const parsedFilters = this.parseFilterBy(filterBy);
        const organizationLevelId = this.toStringValue(parsedFilters.organizationLevelId);

        let params = new HttpParams()
            .set('page', String(page))
            .set('pageSize', String(pageSize));

        if (organizationLevelId != null) {
            params = params.set('organizationLevelId', organizationLevelId);
        }

        if (sort) {
            params = params.set('sort', sort);
        }

        const serverOrderBy = this.toServerOrderBy(orderBy);
        if (serverOrderBy != null) {
            params = params.set('orderBy', serverOrderBy);
        }

        const searchText = this.toStringValue(filter);
        if (searchText != null) {
            params = params.set('filter', searchText);
        }

        // BE (DASH-2582) takes a single `state`; for a partial multi-select send them as an
        // OR list through the grid filterBy dictionary. All (or none) selected = no filter.
        const states = this.toEnumValues(parsedFilters.state);
        const serverFilterBy: Record<string, string> = {};
        if (states.length === 1) {
            params = params.set('state', String(states[0]));
        } else if (states.length > 1 && states.length < this.alertEventStateCount) {
            serverFilterBy['State'] = states.join(',');
        }

        // There is no multi-value severity param (`minSeverity` would also return every
        // higher severity), so a partial selection goes through filterBy like State.
        const severities = this.toEnumValues(parsedFilters.severity);
        if (severities.length && severities.length < this.alertSeverityCount) {
            serverFilterBy['Severity'] = severities.join(',');
        }

        const parameterId = this.toStringValue(parsedFilters.parameterId);
        if (parameterId != null) {
            params = params.set('parameterId', parameterId);
        }

        const startDate = this.toStringValue(parsedFilters.startDate);
        if (startDate != null) {
            params = params.set('startDate', startDate);
        }

        const endDate = this.toStringValue(parsedFilters.endDate);
        if (endDate != null) {
            params = params.set('endDate', endDate);
        }

        if (Object.keys(serverFilterBy).length) {
            params = params.set('filterBy', JSON.stringify(serverFilterBy));
        }

        return this.http
            .get<unknown>(this.getApiUrl('AlertEvents/GetAlertEvents'), { params })
            .pipe(
                map((response) =>
                    this.normalizeAlertEventsResponse(
                        response,
                        filter,
                        orderBy,
                        sort,
                        page,
                        pageSize,
                        parsedFilters,
                    ),
                ),
            );
    }

    getActiveAssignedAlertEvents(): Observable<AppNotification[]> {
        const organizationLevelId = this.toOrganizationLevelIdParam(
            this.commonService.getSelectedOrganizationLevelId(),
        );

        // BE (DASH-2582) takes a single `state`, so the two unacknowledged states are sent
        // as an OR list through the grid filterBy dictionary, same as the multi-select path
        // in get(). Without this the endpoint returned every state and the bell/dropdown
        // (DASH-3250) relied on a client-side filter that never actually ran on real data.
        const unacknowledgedStates = [AlertEventState.Active, AlertEventState.Escalated];

        // Watch-severity alerts are excluded via the BE `minSeverity` filter (DASH-2225),
        // never client-side (DASH-3250 acceptance criteria) — the range above is passed
        // through to normalizeAlertEventsResponse too so a non-paginated fallback response
        // stays consistent with what the server was asked to filter.
        const nonWatchSeverities = [AlertSeverityLevel.Action, AlertSeverityLevel.Alert, AlertSeverityLevel.Critical];
        let params = new HttpParams()
            .set('page', '0')
            .set('pageSize', String(ACTIVE_ALERT_EVENTS_PAGE_SIZE))
            .set('assignedToCurrentUser', 'true')
            .set('minSeverity', String(AlertSeverityLevel.Action))
            .set('filterBy', JSON.stringify({ State: unacknowledgedStates.join(',') }));
        if (organizationLevelId != null) {
            params = params.set('organizationLevelId', organizationLevelId);
        }
        return this.http
            .get<unknown>(this.getApiUrl('AlertEvents/GetAlertEvents'), { params })
            .pipe(
                map(
                    (response) =>
                        this.normalizeAlertEventsResponse(response, '', '', '', 0, 0, {
                            state: unacknowledgedStates.join(','),
                            severity: nonWatchSeverities.join(','),
                        }).data || [],
                ),
            );
    }

    private readonly alertEventStateCount = Object.values(AlertEventState).filter(
        (value) => typeof value === 'number',
    ).length;

    private readonly alertSeverityCount = Object.values(AlertSeverityLevel).filter(
        (value) => typeof value === 'number',
    ).length;

    private readonly serverOrderByMap: Record<string, string> = {
        notificationSentDate: 'StartedAt',
        severity: 'Severity',
        state: 'State',
    };

    private toServerOrderBy(orderBy: string): string | null {
        if (!orderBy) {
            return null;
        }

        return this.serverOrderByMap[orderBy] ?? null;
    }

    private toNumber(value: unknown): number | null {
        if (typeof value === 'number' && !Number.isNaN(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsedValue = Number(value);
            return Number.isNaN(parsedValue) ? null : parsedValue;
        }

        return null;
    }

    private toStringValue(value: unknown): string | null {
        if (typeof value !== 'string') {
            return null;
        }

        const normalizedValue = value.trim();
        return normalizedValue.length ? normalizedValue : null;
    }

    private toOrganizationLevelIdParam(value: unknown): string | null {
        if (value === 0) {
            return null;
        }

        const organizationLevelId = this.toStringValue(value);
        return organizationLevelId === '0' ? null : organizationLevelId;
    }

    getDeliveryLogs(alertEventId: string): Observable<AlertDeliveryLog[]> {
        const params = new HttpParams().set('alertEventId', String(alertEventId));

        return this.http
            .get<unknown>(this.getApiUrl('AlertDeliveryLog/GetDeliveryLog'), { params })
            .pipe(map((response) => this.normalizeDeliveryLogs(response)));
    }

    /**
     * Every notification (message) that referenced the alert event, each with its delivery rows.
     * A failed lookup yields no groups so one bad event can't blank the rest of the chart overlay.
     */
    getDeliveryGroups(alertEventId: string): Observable<NotificationDeliveryGroup[]> {
        const params = new HttpParams().set('alertEventId', alertEventId);

        return this.http
            .get<unknown>(this.getApiUrl('AlertDeliveryLog/GetDeliveryLog'), { params })
            .pipe(
                map((response) => this.normalizeDeliveryGroups(response)),
                catchError(() => of<NotificationDeliveryGroup[]>([])),
            );
    }

    getAlertEventTransitions(alertEventId: string): Observable<AlertStateTransition[]> {
        return this.http
            .get<unknown>(this.getApiUrl(`AlertEventTransitions/${alertEventId}/GetTransitions`))
            .pipe(
                map((response) => this.normalizeStateTransitions(response)),
                catchError(() => of<AlertStateTransition[]>([])),
            );
    }

    private normalizeDeliveryLogs(response: unknown): AlertDeliveryLog[] {
        if (Array.isArray(response)) {
            return response.map((item) => this.mapDeliveryLogItem(item));
        }

        if (!response || typeof response !== 'object') {
            return [];
        }

        const payload = response as { data?: unknown; items?: unknown };
        const rawData = Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload.items)
            ? payload.items
            : [];

        return rawData.map((item) => this.mapDeliveryLogItem(item));
    }

    private normalizeDeliveryGroups(response: unknown): NotificationDeliveryGroup[] {
        if (!Array.isArray(response)) {
            return [];
        }

        return response
            .filter((item): item is NotificationDeliveryGroup => !!item && typeof item === 'object')
            .map((group) => ({
                notificationId: group.notificationId,
                sentAt: group.sentAt,
                alertEventCount: group.alertEventCount,
                deliveries: (Array.isArray(group.deliveries) ? group.deliveries : [])
                    .map((delivery) => this.mapDeliveryLogItem(delivery)),
            }));
    }

    private normalizeStateTransitions(response: unknown): AlertStateTransition[] {
        if (Array.isArray(response)) {
            return response.map((item) => this.mapStateTransitionItem(item));
        }

        if (!response || typeof response !== 'object') {
            return [];
        }

        const payload = response as { data?: unknown; items?: unknown };
        const rawData = Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload.items)
            ? payload.items
            : [];

        return rawData.map((item) => this.mapStateTransitionItem(item));
    }

    acknowledgeAlertEvent(alertEventId: string): Observable<unknown> {
        return this.postAlertTransition(alertEventId, 'Acknowledge');
    }

    resolveAlertEvent(alertEventId: string): Observable<unknown> {
        return this.postAlertTransition(alertEventId, 'Resolve');
    }

    private postAlertTransition(alertEventId: string, action: 'Acknowledge' | 'Resolve'): Observable<unknown> {
        const transitionUrl = this.getApiUrl(`AlertEventTransitions/${alertEventId}/${action}`);
        const transitionPayload = { source: AlertEventTransitionSource.InAppUi };

        return this.http.post(transitionUrl, transitionPayload);
    }

    // A non-2xx (expired/revoked/unknown token) is mapped to null rather than rethrown, so an
    // invalid link renders this page's own "link is no longer valid" state instead of a raw
    // HTTP error.
    getPublicAlertResponsePage(token: string): Observable<PublicAlertResponsePageData | null> {
        const mockToken = this.toMockAlertResponseToken(token);
        if (mockToken) {
            return of(this.buildMockAlertResponsePageData(mockToken)).pipe(delay(300));
        }

        return this.http
            .get<unknown>(this.getAckLinkUrl(token, 'page'))
            .pipe(
                map((response) => this.mapPublicResponsePageData(response)),
                catchError(() => of<PublicAlertResponsePageData | null>(null)),
            );
    }

    // Wired to the real, merged backend (DASH-3074, PR #1364 -
    // AcknowledgementLinkController).
    // `source` says which channel delivered the link; the BE records EmailLink when it is omitted.
    acknowledgePublicAlertResponsePage(
        token: string,
        source?: AlertEventTransitionSource.EmailLink | AlertEventTransitionSource.SmsDeepLink,
    ): Observable<AcknowledgePublicAlertResponseResult> {
        if (this.toMockAlertResponseToken(token)) {
            return of({
                acknowledgedAt: new Date().toISOString(),
                acknowledgedByName: 'Priya Nandakumar',
                transitions: [
                    { alertEventId: 'event-1', nodeName: 'Mill A L2 Centre', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                    { alertEventId: 'event-2', nodeName: 'Mill A L4 Deck', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                    { alertEventId: 'event-3', nodeName: 'Mill A L6 Stack', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                ],
            }).pipe(delay(300));
        }

        return this.http
            .post<AlertEventTransitionResponse[]>(this.getAckLinkUrl(token, 'confirm'), source == null ? {} : { source })
            .pipe(map((transitions) => this.mapAckLinkConfirmResult(transitions ?? [])));
    }

    /**
     * The "Not you?" path. A phone number on a user record is a column to be
     * populated, not a verified fact, so an SMS token can reach a handset its
     * named recipient does not hold. This is how they kill it: it records nothing.
     */
    revokePublicAlertResponseLink(token: string): Observable<unknown> {
        if (this.toMockAlertResponseToken(token)) {
            return of({}).pipe(delay(300));
        }

        return this.http.post(this.getAckLinkUrl(token, 'revoke'), {});
    }

    capturePublicAlertResponse(token: string, payload: CapturePublicAlertResponsePayload): Observable<unknown> {
        if (this.toMockAlertResponseToken(token)) {
            return of({}).pipe(delay(300));
        }

        return this.http.post(this.getAckLinkUrl(token, 'response'), payload);
    }

    private toMockAlertResponseToken(token: string): MockAlertResponseToken | null {
        if (environment.production) {
            return null;
        }

        return (MOCK_ALERT_RESPONSE_TOKENS as readonly string[]).includes(token)
            ? (token as MockAlertResponseToken)
            : null;
    }

    private buildMockAlertResponsePageData(scenario: MockAlertResponseToken): PublicAlertResponsePageData {
        const withCoordinates = scenario !== 'mock-nomap';

        const nodes: PublicAlertResponseNode[] = [
            {
                id: 'node-1',
                name: 'Mill A L2 Centre',
                latitude: withCoordinates ? 46.8123 : null,
                longitude: withCoordinates ? -87.5456 : null,
                isAlerting: true,
                isCoverageLocked: true,
                isReporting: true,
                severity: AlertSeverityLevel.Critical,
                distanceMeters: 0,
                bandLevel: 5,
                readingValue: 241,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
            },
            {
                id: 'node-2',
                name: 'Mill A L4 Deck',
                latitude: withCoordinates ? 46.8131 : null,
                longitude: withCoordinates ? -87.5449 : null,
                isAlerting: true,
                isCoverageLocked: true,
                isReporting: true,
                severity: AlertSeverityLevel.Alert,
                distanceMeters: 42,
                bandLevel: 4,
                readingValue: 168,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
            },
            {
                id: 'node-3',
                name: 'Mill A L6 Stack',
                latitude: withCoordinates ? 46.8127 : null,
                longitude: withCoordinates ? -87.5461 : null,
                isAlerting: true,
                isCoverageLocked: true,
                isReporting: false,
                severity: AlertSeverityLevel.Alert,
                lastReportedAt: new Date(Date.now() - 2 * 60 * 1000),
                distanceMeters: 61,
                bandLevel: 3,
                readingValue: 151,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
            },
            {
                id: 'node-4',
                name: 'Mill A L3 Bagging',
                latitude: withCoordinates ? 46.8119 : null,
                longitude: withCoordinates ? -87.5438 : null,
                isAlerting: false,
                isReporting: true,
                distanceMeters: 18,
                bandLevel: 2,
            },
            {
                id: 'node-5',
                name: 'Mill A L1 Crusher',
                latitude: withCoordinates ? 46.8109 : null,
                longitude: withCoordinates ? -87.5471 : null,
                isAlerting: false,
                isReporting: true,
                distanceMeters: 31,
                bandLevel: 1,
            },
            {
                id: 'node-6',
                name: 'Crusher Bay 3',
                latitude: withCoordinates ? 46.8141 : null,
                longitude: withCoordinates ? -87.5433 : null,
                isAlerting: false,
                isReporting: true,
                distanceMeters: 96,
                bandLevel: 3,
            },
            {
                id: 'node-7',
                name: 'Haul Road 2',
                latitude: withCoordinates ? 46.8098 : null,
                longitude: withCoordinates ? -87.5418 : null,
                isAlerting: false,
                isReporting: true,
                distanceMeters: 140,
                bandLevel: 2,
            },
            {
                id: 'node-8',
                name: 'ROM Pad 1',
                latitude: null,
                longitude: null,
                isAlerting: false,
                isReporting: true,
                distanceMeters: null,
                bandLevel: 1,
            },
        ];

        const controls: PublicAlertResponseControlOption[] = [
            {
                id: 'control-1',
                name: 'Mill throttle - reduced feed',
                categoryName: 'Engineering control',
                isSuggested: true,
                isRecentlyUsed: false,
            },
            {
                id: 'control-2',
                name: 'Water cart - haul road and pad',
                categoryName: 'Suppressant',
                isSuggested: false,
                isRecentlyUsed: true,
                lastUsedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
                lastUsedByName: 'D. Meyers',
            },
            {
                id: 'control-3',
                name: 'Baghouse - filter change',
                categoryName: 'Engineering control',
                isSuggested: false,
                isRecentlyUsed: true,
                lastUsedAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
            },
            {
                id: 'control-4',
                name: 'Shut down - stop the plant item',
                categoryName: 'Administrative',
                isSuggested: false,
                isRecentlyUsed: false,
            },
            {
                id: 'control-5',
                name: 'Respiratory protection - P2 issued',
                categoryName: 'PPE',
                isSuggested: false,
                isRecentlyUsed: false,
            },
        ];

        const events: PublicAlertResponseEvent[] = [
            {
                id: 'event-1',
                nodeId: 'node-1',
                nodeName: 'Mill A L2 Centre',
                state: AlertEventState.Active,
                severity: AlertSeverityLevel.Critical,
                readingValue: 241,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
                bandLevel: 5,
                aggregationLabel: '15-min mean',
                trendLabel: 'rising',
                startedAt: new Date(Date.now() - 5 * 60 * 1000),
            },
            {
                id: 'event-2',
                nodeId: 'node-2',
                nodeName: 'Mill A L4 Deck',
                state: AlertEventState.Active,
                severity: AlertSeverityLevel.Alert,
                readingValue: 168,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
                bandLevel: 4,
                aggregationLabel: '15-min mean',
                trendLabel: 'rising',
                startedAt: new Date(Date.now() - 5 * 60 * 1000),
            },
            {
                id: 'event-3',
                nodeId: 'node-3',
                nodeName: 'Mill A L6 Stack',
                state: AlertEventState.Active,
                severity: AlertSeverityLevel.Alert,
                readingValue: 151,
                thresholdValue: 150,
                unitLabel: 'µg/m³',
                bandLevel: 3,
                aggregationLabel: '15-min mean',
                startedAt: new Date(Date.now() - 5 * 60 * 1000),
                lastReportedAt: new Date(Date.now() - 2 * 60 * 1000),
            },
        ];

        const isAcknowledged = scenario === 'mock-acked' || scenario === 'mock-nocapture';

        return {
            notificationId: 'notification-1',
            ruleName: 'PM10 - Mill fenceline, 15-min',
            siteName: 'Tilden Mine, North Operations',
            severity: AlertSeverityLevel.Critical,
            detectedAt: new Date(Date.now() - 5 * 60 * 1000),
            recipientName: 'Priya Nandakumar',
            recipientMaskedPhone: '****4821',
            tokenExpiresAt: new Date(Date.now() + 30 * 60 * 1000),
            ruleActionInstruction: 'Confirm baghouse differential pressure, then switch the mill to reduced feed.',
            suggestedControlName: 'Mill throttle - reduced feed',
            canAcknowledge: true,
            canCaptureResponse: scenario !== 'mock-nocapture',
            totalControlCount: 142,
            isAcknowledged,
            acknowledgedAt: isAcknowledged ? new Date(Date.now() - 3 * 60 * 1000) : null,
            acknowledgedByName: isAcknowledged ? 'Priya Nandakumar' : undefined,
            events,
            nodes,
            controls,
            transitions: isAcknowledged
                ? [
                      { alertEventId: 'event-1', nodeName: 'Mill A L2 Centre', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                      { alertEventId: 'event-2', nodeName: 'Mill A L4 Deck', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                      { alertEventId: 'event-3', nodeName: 'Mill A L6 Stack', fromState: AlertEventState.Active, toState: AlertEventState.Acknowledged },
                  ]
                : [],
        };
    }

    // Maps the page-data response. It carries no per-node acknowledged-by/acknowledged-at/
    // transition history - canAcknowledge is the single source of truth for whether this
    // notification is already acknowledged (by any token/recipient/the in-app UI), so
    // isAcknowledged is derived from it rather than carried as its own field.
    private mapPublicResponsePageData(response: unknown): PublicAlertResponsePageData | null {
        if (!response || typeof response !== 'object') {
            return null;
        }

        const raw = response as Record<string, unknown>;
        const suggestedControlName = this.toStringValue(raw.suggestedControlName) ?? undefined;
        const events = this.toIdentifiedArray(raw.events, (item) => this.mapPublicResponseEvent(item));
        const nodes = this.toIdentifiedArray(raw.nodes, (item) => this.mapPublicResponseNode(item, events));
        const controls = this.toIdentifiedArray(raw.controls, (item) =>
            this.mapPublicResponseControl(item, suggestedControlName),
        );

        return {
            notificationId: this.toStringValue(raw.notificationId) ?? undefined,
            ruleName: this.toStringValue(raw.ruleName) ?? undefined,
            siteName: this.toStringValue(raw.siteName) ?? undefined,
            severity: (raw.severity as AlertSeverityLevel | string) ?? AlertSeverityLevel.Watch,
            detectedAt: (raw.detectedAt as Date | string) ?? '',
            recipientName: this.toStringValue(raw.recipientName) ?? undefined,
            recipientMaskedPhone: this.toStringValue(raw.recipientMaskedPhone) ?? undefined,
            tokenExpiresAt: raw.tokenExpiresAt as Date | string | undefined,
            ruleActionInstruction: this.toStringValue(raw.ruleActionInstruction) ?? undefined,
            suggestedControlName,
            canAcknowledge: raw.canAcknowledge !== false,
            canCaptureResponse: raw.canCaptureResponse === true,
            totalControlCount: controls.length,
            isAcknowledged: raw.canAcknowledge === false,
            events,
            nodes,
            controls,
            submittedResponse: this.mapSubmittedResponse(raw.response),
        };
    }

    private mapSubmittedResponse(raw: unknown): PublicAlertResponseSubmitted | null {
        if (!raw || typeof raw !== 'object') {
            return null;
        }

        const item = raw as Record<string, unknown>;
        const controlIds = Array.isArray(item.controlIds)
            ? item.controlIds.map((id) => this.toStringValue(id)).filter((id): id is string => !!id)
            : [];
        const coveredNodeIds = Array.isArray(item.coveredNodeIds)
            ? item.coveredNodeIds.map((id) => this.toStringValue(id)).filter((id): id is string => !!id)
            : [];

        return {
            controlIds,
            coveredNodeIds,
            startedAt: (item.startedAt as Date | string) ?? '',
            responseNote: this.toStringValue(item.responseNote) ?? undefined,
        };
    }

    /**
     * A row the server sent without its own id cannot be selected, submitted or
     * tracked, so it is dropped here rather than carried with a fabricated key.
     */
    private toIdentifiedArray<T>(
        raw: unknown,
        mapItem: (item: unknown) => T,
        idKey: keyof T = 'id' as keyof T,
    ): T[] {
        if (!Array.isArray(raw)) {
            return [];
        }

        return raw.map(mapItem).filter((item) => !!item[idKey]);
    }

    // PublicAlertEventReadingDto carries alertEventId (not id) and peakValue/peakAt (not
    // readingValue/a threshold/band/trend breakdown) - none of the latter have a backend
    // source yet, so they are left undefined rather than defaulted.
    private mapPublicResponseEvent(item: unknown): PublicAlertResponseEvent {
        const raw = (item ?? {}) as Record<string, unknown>;

        return {
            id: this.toStringValue(raw.alertEventId) ?? undefined,
            nodeId: this.toStringValue(raw.nodeId) ?? undefined,
            nodeName: this.toStringValue(raw.nodeName) ?? '',
            state: (raw.state as AlertEventState | string) ?? AlertEventState.Active,
            severity: (raw.severity as AlertSeverityLevel | string) ?? AlertSeverityLevel.Watch,
            readingValue: raw.peakValue as number | null,
            startedAt: (raw.startedAt as Date | string) ?? '',
        };
    }

    // PublicAlertNodeDto carries nodeId (not id) and no severity/reading/band fields of its
    // own - severity is derived from this node's own alert event (real, not fabricated) so
    // the marker/coverage rows can still colour by severity.
    private mapPublicResponseNode(
        item: unknown,
        events: PublicAlertResponseEvent[],
    ): PublicAlertResponseNode {
        const raw = (item ?? {}) as Record<string, unknown>;
        const id = this.toStringValue(raw.nodeId) ?? undefined;
        const severity = events.find((event) => event.nodeId === id)?.severity ?? null;

        return {
            id,
            name: this.toStringValue(raw.name) ?? '',
            latitude: raw.latitude as number | null,
            longitude: raw.longitude as number | null,
            isAlerting: raw.isAlerting === true,
            isCoverageLocked: raw.isAlerting === true,
            isReporting: raw.isReporting !== false,
            severity,
            distanceMeters: raw.distanceMeters as number | null,
        };
    }

    // PublicAlertControlDto carries controlId (not id) and no suggested/recently-used
    // flags - isSuggested is derived by matching the page-level SuggestedControlName
    // (the only "which control does this rule suggest" signal the backend sends).
    private mapPublicResponseControl(
        item: unknown,
        suggestedControlName: string | undefined,
    ): PublicAlertResponseControlOption {
        const raw = (item ?? {}) as Record<string, unknown>;
        const name = this.toStringValue(raw.name) ?? '';

        return {
            id: this.toStringValue(raw.controlId) ?? undefined,
            name,
            isSuggested: !!suggestedControlName && name === suggestedControlName,
            isRecentlyUsed: false,
        };
    }

    // AcknowledgementLinkController.Confirm returns a raw AlertEventTransitionDto[] -
    // no wrapping object. acknowledgedAt/acknowledgedByName are read off the first
    // transition rather than a top-level field, since the real response has none;
    // an all-skipped re-ack (every event already acknowledged) returns an empty
    // array, so acknowledgedAt falls back to the time this confirm call completed.
    private mapAckLinkConfirmResult(
        transitions: AlertEventTransitionResponse[],
    ): AcknowledgePublicAlertResponseResult {
        const first = transitions[0];

        return {
            acknowledgedAt: first?.transitionedAt ?? new Date().toISOString(),
            acknowledgedByName: this.toStringValue(first?.transitionedByUserName) ?? undefined,
            transitions: transitions.map((transition) => ({
                alertEventId: this.toStringValue(transition.alertEventId) ?? undefined,
                nodeName: this.toStringValue(transition.nodeName) ?? undefined,
                fromState: transition.fromState as AlertEventState,
                toState: transition.toState as AlertEventState,
            })),
        };
    }

    private getAckLinkUrl(token: string, action?: 'confirm' | 'page' | 'response' | 'revoke'): string {
        const base = `${getRegionBaseUrl()}/${ACK_LINK_ROUTE_SEGMENT}/${encodeURIComponent(token)}`;
        return action ? `${base}/${action}` : base;
    }

    private parseFilterBy(filterBy: string): Record<string, unknown> {
        if (!filterBy) {
            return {};
        }

        try {
            return JSON.parse(filterBy) as Record<string, unknown>;
        } catch {
            return {};
        }
    }

    private normalizeAlertEventsResponse(
        response: unknown,
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        parsedFilters: Record<string, unknown>,
    ): GridModel<AppNotification> {
        const emptyResult = new GridModel<AppNotification>([], 0);

        if (Array.isArray(response)) {
            const mappedData = response.map((item) => this.mapAlertEventItem(item));
            return this.buildClientSideGrid(
                mappedData,
                filter,
                orderBy,
                sort,
                page,
                pageSize,
                parsedFilters,
            );
        }

        if (!response || typeof response !== 'object') {
            return emptyResult;
        }

        const payload = response as {
            data?: unknown;
            count?: unknown;
            totalCount?: unknown;
            items?: unknown;
            success?: boolean;
            message?: string;
        };

        const rawData = Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload.items)
            ? payload.items
            : [];

        const mappedData = rawData.map((item) => this.mapAlertEventItem(item));

        const resolvedCount =
            typeof payload.count === 'number'
                ? payload.count
                : typeof payload.totalCount === 'number'
                ? payload.totalCount
                : mappedData.length;

        if (
            typeof payload.count !== 'number' &&
            typeof payload.totalCount !== 'number'
        ) {
            return this.buildClientSideGrid(
                mappedData,
                filter,
                orderBy,
                sort,
                page,
                pageSize,
                parsedFilters,
            );
        }

        // Server-paginated envelope (DASH-2582): the BE has already applied filtering,
        // sorting and paging. Trust it verbatim — re-filtering here would drop rows from
        // the current page and desync the total count from the paginator.
        return {
            data: mappedData,
            count: resolvedCount,
            success: typeof payload.success === 'boolean' ? payload.success : true,
            message: typeof payload.message === 'string' ? payload.message : '',
        };
    }

    private buildClientSideGrid(
        notifications: AppNotification[],
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        parsedFilters: Record<string, unknown>,
    ): GridModel<AppNotification> {
        const filteredNotifications = notifications.filter((notification) =>
            this.matchesClientSideFilters(notification, filter, parsedFilters),
        );
        const sortedNotifications = this.sortNotifications(
            filteredNotifications,
            orderBy,
            sort,
        );
        const startIndex = Math.max(page, 0) * Math.max(pageSize, 0);
        const pagedNotifications =
            pageSize > 0
                ? sortedNotifications.slice(startIndex, startIndex + pageSize)
                : sortedNotifications;

        return {
            data: pagedNotifications,
            count: sortedNotifications.length,
            success: true,
            message: '',
        };
    }

    private matchesClientSideFilters(
        notification: AppNotification,
        filter: string,
        parsedFilters: Record<string, unknown>,
    ): boolean {
        const textFilter = filter.trim().toLowerCase();
        if (textFilter) {
            const searchableValues = [
                notification.notificationName,
                notification.parameterName,
                notification.siteName,
                notification.endPoint,
                notification.sensorId,
            ]
                .filter((value): value is string => typeof value === 'string')
                .map((value) => value.toLowerCase());

            if (!searchableValues.some((value) => value.includes(textFilter))) {
                return false;
            }
        }

        const states = this.toEnumValues(parsedFilters.state);
        if (states.length && !states.includes(notification.state ?? -1)) {
            return false;
        }

        const severities = this.toEnumValues(parsedFilters.severity);
        if (severities.length && !severities.includes(notification.severity ?? -1)) {
            return false;
        }

        const parameterId = this.toStringValue(parsedFilters.parameterId);
        if (parameterId != null && notification.parameterId !== parameterId) {
            return false;
        }

        const siteId = this.toStringValue(
            parsedFilters.siteId ?? parsedFilters.organizationLevelId,
        );
        if (siteId != null && notification.siteId !== siteId) {
            return false;
        }

        const notificationDate = this.resolveNotificationDate(notification);
        const startDate = this.toDateValue(parsedFilters.startDate);
        if (startDate != null && (notificationDate == null || notificationDate < startDate)) {
            return false;
        }

        const endDate = this.toDateValue(parsedFilters.endDate);
        if (endDate != null && (notificationDate == null || notificationDate > endDate)) {
            return false;
        }

        return true;
    }

    private sortNotifications(
        notifications: AppNotification[],
        orderBy: string,
        sort: string,
    ): AppNotification[] {
        const sortDirection = sort.toLowerCase() === 'asc' ? 1 : -1;
        const normalizedOrderBy = orderBy || 'notificationSentDate';

        return [...notifications].sort((left, right) => {
            const leftValue = this.resolveSortValue(left, normalizedOrderBy);
            const rightValue = this.resolveSortValue(right, normalizedOrderBy);

            if (leftValue == null && rightValue == null) {
                return 0;
            }

            if (leftValue == null) {
                return 1;
            }

            if (rightValue == null) {
                return -1;
            }

            if (typeof leftValue === 'number' && typeof rightValue === 'number') {
                return (leftValue - rightValue) * sortDirection;
            }

            return String(leftValue).localeCompare(String(rightValue)) * sortDirection;
        });
    }

    private resolveSortValue(
        notification: AppNotification,
        orderBy: string,
    ): number | string | null {
        switch (orderBy) {
            case 'notificationSentDate':
                return this.resolveNotificationDate(notification)?.getTime() ?? null;
            case 'severity':
                return notification.severity ?? null;
            case 'state':
                return notification.state ?? null;
            case 'endPoint.Name':
                return notification.endPoint ?? '';
            case 'Parameter.AnalyteName':
                return notification.parameterName ?? '';
            default: {
                const directValue = notification[orderBy as keyof AppNotification];
                if (
                    typeof directValue === 'number' ||
                    typeof directValue === 'string'
                ) {
                    return directValue;
                }

                return null;
            }
        }
    }

    private resolveNotificationDate(notification: AppNotification): Date | null {
        return this.toDateValue(
            notification.startedAt ??
                notification.sentDate ??
                notification.notificationSentDate,
        );
    }

    private toDateValue(value: unknown): Date | null {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'number' || typeof value === 'string') {
            const parsedDate = new Date(value);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        return null;
    }

    private toEnumValues(value: unknown): number[] {
        if (typeof value === 'number' && !Number.isNaN(value)) {
            return [value];
        }

        if (typeof value !== 'string') {
            return [];
        }

        return value
            .split(',')
            .map((item) => Number(item.trim()))
            .filter((item) => !Number.isNaN(item));
    }

    private mapAlertEventItem(raw: unknown): AppNotification {
        const item = raw as AlertEventResponse;
        const siteId = item.siteId ?? item.organizationLevelId ?? undefined;
        const stateTransitions = item.stateTransitions ?? item.transitions;
        const stateUpdatedAt = item.stateUpdatedAt ?? item.dateUpdated;

        return {
            id: item.id,
            notificationName: item.notificationName,
            sensorId: item.sensorId,
            parameterId: item.parameterId ?? undefined,
            parameterName: item.parameterName,
            organizationLevelId: siteId,
            siteId,
            siteName: item.siteName,
            endPointId: item.customEntityId ?? item.endPointId ?? undefined,
            severity: this.normalizeSeverityValue(item.severity),
            state: this.normalizeStateValue(item.state),
            deliveryStatus: this.normalizeDeliveryStatusValue(item.deliveryStatus),
            notificationSentDate: item.startedAt as Date | string,
            sentDate: item.startedAt as string,
            startedAt: item.startedAt as string,
            peakAt: item.peakAt as string,
            peakValue: item.peakValue as number,
            endedAt: item.endedAt as string | null,
            stateUpdatedAt: stateUpdatedAt as Date | string,
            deliveryLogs: (item.deliveryLogs || []).map((deliveryLog) => this.mapDeliveryLogItem(deliveryLog)),
            stateTransitions: (stateTransitions || []).map((transition) => this.mapStateTransitionItem(transition)),
            isSuppressed: item.isSuppressed,
            suppressionReason: item.suppressionReason,
        } as AppNotification;
    }

    private mapStateTransitionItem(raw: unknown): AlertStateTransition {
        const item = raw as AlertEventTransitionResponse;
        const fromState = this.normalizeStateValue(item.fromState);
        const toState = this.normalizeStateValue(item.toState);

        return {
            id: item.id,
            alertEventId: item.alertEventId,
            ...(fromState != null ? { fromState } : {}),
            toState: (toState ?? AlertEventState.Active) as AlertEventState,
            transitionedAt: item.transitionedAt,
            transitionedByUserId: item.transitionedByUserId,
            transitionedByUserName: item.transitionedByUserName,
            source: item.source,
            comment: item.comment,
        } as AlertStateTransition;
    }

    private mapDeliveryLogItem(raw: unknown): AlertDeliveryLog {
        const item = raw as AlertDeliveryLog;

        return {
            ...item,
            status: this.normalizeDeliveryStatusValue(item.status),
            channel: this.normalizeDeliveryChannelValue(item.channel),
            attemptedAt: item.attemptedAt,
            sentAt: item.sentAt ?? item.attemptedAt,
        };
    }

    private normalizeSeverityValue(value: unknown): AlertSeverityLevel | undefined {
        const parsedValue = this.toNumber(value);
        if (parsedValue == null) {
            return undefined;
        }

        if (parsedValue >= AlertSeverityLevel.Watch && parsedValue <= AlertSeverityLevel.Critical) {
            return parsedValue as AlertSeverityLevel;
        }

        const legacyValue = parsedValue + 1;
        if (legacyValue >= AlertSeverityLevel.Watch && legacyValue <= AlertSeverityLevel.Critical) {
            return legacyValue as AlertSeverityLevel;
        }

        return undefined;
    }

    private normalizeStateValue(value: unknown): AlertEventState | undefined {
        const parsedValue = this.toNumber(value);
        if (parsedValue == null) {
            return undefined;
        }

        if (parsedValue >= AlertEventState.Active && parsedValue <= AlertEventState.Escalated) {
            return parsedValue as AlertEventState;
        }

        const legacyValue = parsedValue + 1;
        if (legacyValue >= AlertEventState.Active && legacyValue <= AlertEventState.Escalated) {
            return legacyValue as AlertEventState;
        }

        return undefined;
    }

    private normalizeDeliveryStatusValue(value: unknown): AlertDeliveryStatus | string | number {
        const parsedValue = this.toNumber(value);
        if (parsedValue == null) {
            return typeof value === 'string' ? value : AlertDeliveryStatus.Pending;
        }

        if (parsedValue >= AlertDeliveryStatus.Pending && parsedValue <= AlertDeliveryStatus.Opened) {
            return parsedValue as AlertDeliveryStatus;
        }

        const legacyValue = parsedValue + 1;
        if (legacyValue >= AlertDeliveryStatus.Pending && legacyValue <= AlertDeliveryStatus.Opened) {
            return legacyValue as AlertDeliveryStatus;
        }

        return parsedValue;
    }

    private normalizeDeliveryChannelValue(value: unknown): AlertDeliveryChannel | string | number {
        const parsedValue = this.toNumber(value);
        if (parsedValue == null) {
            return typeof value === 'string' ? value : AlertDeliveryChannel.Email;
        }

        if (parsedValue >= AlertDeliveryChannel.Email && parsedValue <= AlertDeliveryChannel.Push) {
            return parsedValue as AlertDeliveryChannel;
        }

        return parsedValue;
    }

    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http
            .get<{
                [property: string]: ColumnDetails;
            }>(this.getApiUrl('Notification/GetFieldConfiguration'))
            .pipe(catchError(() => of({})));
    }

    /**
     * Alert events for a single node, scoped to the chart's date range.
     *
     * Repointed off the legacy `Notification/GetFilteredNotifications` endpoint (DASH-2844):
     * its `NotificationDto` carried no Severity/State/StartedAt/EndedAt/Peak, so the node-detail
     * chart fabricated those (Watch/Active/full-window bands). `AlertEvents/GetAlertEvents`
     * returns the real lifecycle fields via `mapAlertEventItem`, narrowed to the node server-side
     * by `nodeId` (the node's CustomEntityId).
     */
    getNodeAlertEvents(
        filters: FilteredNotificationPayload,
    ): Observable<AppNotification[]> {
        let params = new HttpParams()
            .set('page', '0')
            .set('pageSize', String(NODE_ALERT_EVENTS_PAGE_SIZE));

        // Omit organizationLevelId when unset — a falsy default (0 / '') can be read
        // as a wildcard/root scope by the BE and widen the query past the user's node.
        if (filters.organizationLevelId != null) {
            params = params.set('organizationLevelId', String(filters.organizationLevelId));
        }

        if (filters.nodeId) {
            params = params.set('nodeId', filters.nodeId);
        }

        if (filters.analyteId) {
            params = params.set('parameterId', String(filters.analyteId));
        }

        const startDate = this.toIsoDate(filters.startDate);
        if (startDate != null) {
            params = params.set('startDate', startDate);
        }

        const endDate = this.toIsoDate(filters.endDate);
        if (endDate != null) {
            params = params.set('endDate', endDate);
        }

        return this.http
            .get<unknown>(this.getApiUrl('AlertEvents/GetAlertEvents'), { params })
            .pipe(
                map((response) => this.normalizeAlertEventsResponse(response, '', '', '', 0, 0, {})),
                tap((grid) => {
                    // When the server's total reaches the requested page size the response
                    // was truncated — tell the user rather than silently dropping events.
                    if (grid.count >= NODE_ALERT_EVENTS_PAGE_SIZE) {
                        this.toastService.showWarning(
                            'Showing the first {{count}} alert events only. Narrow the date range to see the rest.',
                            true,
                            { count: NODE_ALERT_EVENTS_PAGE_SIZE },
                        );
                    }
                }),
                map((grid) => grid.data ?? []),
                catchError(() => of([])),
            );
    }

    private toIsoDate(value: number | null | undefined): string | null {
        if (value == null || value === 0) {
            return null;
        }

        const normalizedValue = this.dateFormatService.normalizeTimestamp(value);
        const parsedDate = new Date(normalizedValue);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
    }

    delete(id: string) {
        return this.http.delete(
            this.getApiUrl(`Notification/DeleteNotification?id=${id}`),
        );
    }
}
